import { useState, useEffect, useCallback } from "react";
import { api } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { CheckCircle2, XCircle, ShieldCheck, Eye, Clock, AlertTriangle, FileText } from "lucide-react";

/**
 * Admin → Doc Verification tab (Phase C).
 *
 * Workflow:
 *   - Fetch /api/admin/user-documents?status=pending (oldest first).
 *   - For each pending doc, show partner identity, doc type, file preview,
 *     upload date, valid_till (if set).
 *   - Approve → POST /verify (optional notes). Email partner.
 *   - Reject  → POST /reject (notes REQUIRED). Email partner with reason.
 *   - Counters across pending/verified/rejected shown in header so admin
 *     can switch tabs.
 */

const TABS = [
  { key: "pending",  label: "Pending review",  icon: Clock,         tone: "amber"   },
  { key: "verified", label: "Verified",        icon: ShieldCheck,   tone: "emerald" },
  { key: "rejected", label: "Rejected",        icon: AlertTriangle, tone: "red"     },
];

const BACKEND_ORIGIN = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

function _absUrl(u) {
  if (!u) return "";
  if (/^https?:\/\//i.test(u)) return u;
  if (u.startsWith("/api/")) return `${BACKEND_ORIGIN}${u}`;
  return u;
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

export default function DocVerificationTab() {
  const [activeTab, setActiveTab] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ documents: [], counts: { pending: 0, verified: 0, rejected: 0 }, doc_types: {} });
  const [rejectModal, setRejectModal] = useState(null); // { docId, label, notes }
  const [actioning, setActioning] = useState(null); // doc id currently being acted upon

  const load = useCallback(async (status = activeTab) => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/user-documents?status=${status}&limit=200`);
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load queue");
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => { load(activeTab); }, [activeTab, load]);

  const handleApprove = async (doc) => {
    setActioning(doc.id);
    try {
      const r = await api.post(`/admin/user-documents/${doc.id}/verify`, {});
      const emailStatus = r.data.email_status;
      toast.success(`Approved — partner ${emailStatus === "sent" ? "notified" : emailStatus === "mocked" ? "email queued (mock)" : "(email skipped)"}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Approve failed");
    } finally {
      setActioning(null);
    }
  };

  const handleReject = async () => {
    if (!rejectModal) return;
    if (!rejectModal.notes.trim()) {
      toast.error("Reason is required");
      return;
    }
    setActioning(rejectModal.docId);
    try {
      const r = await api.post(`/admin/user-documents/${rejectModal.docId}/reject`, { notes: rejectModal.notes.trim() });
      const emailStatus = r.data.email_status;
      toast.success(`Rejected — partner ${emailStatus === "sent" ? "notified by email" : "(email skipped)"}`);
      setRejectModal(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reject failed");
    } finally {
      setActioning(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-doc-verification-tab-content">
      {/* Tab strip with counts */}
      <div className="flex flex-wrap gap-2 border-b border-[#E5E3DB] pb-3">
        {TABS.map(t => {
          const Icon = t.icon;
          const isActive = activeTab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-colors ${isActive ? "bg-[#2C5F2D] text-white" : "bg-white text-[#5C6B60] border border-[#E5E3DB] hover:border-[#2C5F2D]"}`}
              data-testid={`doc-verify-tab-${t.key}`}
            >
              <Icon className="w-4 h-4" />
              {t.label}
              <span className={`ml-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${isActive ? "bg-white/20 text-white" : "bg-[#F3F2EE] text-[#5C6B60]"}`}>{data.counts[t.key] || 0}</span>
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="text-center py-12 text-[#8A998E]">Loading queue…</div>
      ) : data.documents.length === 0 ? (
        <div className="text-center py-16 bg-white border border-dashed border-[#E5E3DB] rounded-xl">
          <CheckCircle2 className="w-12 h-12 text-[#A3B8AB] mx-auto mb-3" />
          <h3 className="font-['Outfit'] font-bold text-[#1A231C]">Inbox zero</h3>
          <p className="text-sm text-[#8A998E] mt-1">No {activeTab} documents right now.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {data.documents.map(doc => (
            <DocCard
              key={doc.id}
              doc={doc}
              docTypes={data.doc_types}
              activeTab={activeTab}
              actioning={actioning}
              onApprove={() => handleApprove(doc)}
              onReject={() => setRejectModal({ docId: doc.id, label: doc.label, notes: "" })}
            />
          ))}
        </div>
      )}

      {/* Reject modal */}
      {rejectModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={() => setRejectModal(null)}>
          <div className="bg-white rounded-xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()} data-testid="doc-verify-reject-modal">
            <h3 className="font-['Outfit'] text-lg font-bold text-[#1A231C] mb-1">Reject {rejectModal.label}</h3>
            <p className="text-sm text-[#5C6B60] mb-4">The partner will receive this exact reason via email so they know what to fix.</p>
            <Textarea
              value={rejectModal.notes}
              onChange={e => setRejectModal({ ...rejectModal, notes: e.target.value })}
              placeholder="e.g. PAN image is blurry — please upload a clear, unrotated scan."
              className="bg-white border-[#E5E3DB] rounded-xl min-h-[110px]"
              data-testid="doc-verify-reject-reason"
              autoFocus
            />
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="ghost" onClick={() => setRejectModal(null)} data-testid="doc-verify-reject-cancel">Cancel</Button>
              <Button onClick={handleReject} disabled={!rejectModal.notes.trim() || actioning === rejectModal.docId} className="rounded-full bg-[#B91C1C] hover:bg-[#7F1D1D] text-white" data-testid="doc-verify-reject-confirm">
                <XCircle className="w-4 h-4 mr-2" />{actioning === rejectModal.docId ? "Rejecting…" : "Reject + Email partner"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DocCard({ doc, docTypes, activeTab, actioning, onApprove, onReject }) {
  const typeMeta = docTypes[doc.doc_type] || {};
  const isImage = (doc.content_type || "").startsWith("image/");
  const previewUrl = _absUrl(doc.url);

  return (
    <div className="bg-white border border-[#E5E3DB] rounded-xl p-5 flex flex-col gap-4" data-testid={`doc-card-${doc.id}`}>
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-[#F3F2EE] flex items-center justify-center text-[#5C6B60] flex-shrink-0">
          <FileText className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-['Outfit'] font-bold text-[#1A231C] text-[15px] flex items-center gap-2 flex-wrap">
            {doc.label}
            {typeMeta.critical && <span className="bg-[#FEE2E2] text-[#B91C1C] text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider">Critical</span>}
            <span className="text-[10px] uppercase tracking-wider text-[#8A998E] font-semibold bg-[#F3F2EE] px-2 py-0.5 rounded-full">{typeMeta.category}</span>
          </div>
          <div className="text-xs text-[#5C6B60] mt-1 truncate">
            <span className="font-semibold">{doc.partner_business_name || doc.partner_name}</span>
            {doc.partner_business_name && doc.partner_name && doc.partner_business_name !== doc.partner_name ? <span> · {doc.partner_name}</span> : null}
          </div>
          <div className="text-[11px] text-[#8A998E] truncate">{doc.partner_email}{doc.partner_phone ? ` · ${doc.partner_phone}` : ""}</div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 text-[11px] text-[#5C6B60]">
        <div>
          <div className="uppercase tracking-wider font-semibold text-[10px] text-[#8A998E]">Uploaded</div>
          <div>{formatDate(doc.created_at)}</div>
        </div>
        {typeMeta.has_expiry !== false && (
          <div>
            <div className="uppercase tracking-wider font-semibold text-[10px] text-[#8A998E]">Valid till</div>
            <div>{doc.valid_till ? formatDate(doc.valid_till) : <span className="text-[#A8A8A8]">Not set</span>}</div>
          </div>
        )}
        <div>
          <div className="uppercase tracking-wider font-semibold text-[10px] text-[#8A998E]">File</div>
          <div className="truncate">{doc.filename} · {Math.round((doc.size || 0) / 1024)} KB</div>
        </div>
        <div>
          <div className="uppercase tracking-wider font-semibold text-[10px] text-[#8A998E]">Status</div>
          <div className="capitalize font-semibold">{doc.verification_status}</div>
        </div>
      </div>

      {doc.verification_status === "rejected" && doc.verification_notes && (
        <div className="bg-[#FEE2E2] border border-[#FECACA] rounded-lg p-3 text-xs text-[#7F1D1D]" data-testid={`doc-card-reject-reason-${doc.id}`}>
          <span className="font-bold uppercase tracking-wider text-[10px]">Rejection reason:</span><br />
          {doc.verification_notes}
        </div>
      )}

      {previewUrl && (
        isImage ? (
          <a href={previewUrl} target="_blank" rel="noopener noreferrer" className="block">
            <img src={previewUrl} alt={doc.label} className="w-full h-40 object-cover rounded-lg border border-[#E5E3DB]" />
          </a>
        ) : (
          <a href={previewUrl} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 h-20 bg-[#F3F2EE] rounded-lg text-[#2C5F2D] font-semibold text-sm hover:bg-[#E5E3DB] transition-colors" data-testid={`doc-card-pdf-view-${doc.id}`}>
            <Eye className="w-4 h-4" /> Open PDF
          </a>
        )
      )}

      {activeTab === "pending" && (
        <div className="flex gap-2 pt-2 border-t border-[#E5E3DB]">
          <Button onClick={onApprove} disabled={actioning === doc.id} className="flex-1 rounded-full bg-[#2C5F2D] hover:bg-[#214A22] text-white" data-testid={`doc-card-approve-${doc.id}`}>
            <CheckCircle2 className="w-4 h-4 mr-2" />{actioning === doc.id ? "Approving…" : "Approve"}
          </Button>
          <Button onClick={onReject} disabled={actioning === doc.id} variant="outline" className="flex-1 rounded-full border-[#B91C1C] text-[#B91C1C] hover:bg-[#FEE2E2]" data-testid={`doc-card-reject-${doc.id}`}>
            <XCircle className="w-4 h-4 mr-2" />Reject
          </Button>
        </div>
      )}
    </div>
  );
}
